import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Define LocationPreference enum
const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Define grouped area combinations
const AREA_GROUPS = {
  KIAWAH_SEABROOK: {
    value: 'KIAWAH_SEABROOK',
    label: 'Kiawah - Seabrook',
    areas: [LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND],
    isGroup: true
  },
  IOP_WILD_DUNES: {
    value: 'IOP_WILD_DUNES', 
    label: 'Isle of Palms & Isle of Palms - Wild Dunes',
    areas: [LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES],
    isGroup: true
  }
};

// Available areas array with individual areas and grouped options
const AVAILABLE_AREAS = [
  // Grouped options at the top
  AREA_GROUPS.KIAWAH_SEABROOK,
  AREA_GROUPS.IOP_WILD_DUNES,
  // Divider
  { isDivider: true },
  // Individual areas
  { value: LocationPreference.KIAWAH, label: 'Kiawah', isGroup: false },
  { value: LocationPreference.ISLE_OF_PALMS, label: 'Isle of Palms', isGroup: false },
  { value: LocationPreference.ISLE_OF_PALMS_WILD_DUNES, label: 'Isle of Palms - Wild Dunes', isGroup: false },
  { value: LocationPreference.SEABROOK_ISLAND, label: 'Seabrook Island', isGroup: false }
];

export default function AssignmentsAreaFilter({ selectedAreas, onChange, hasUserInteracted = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const componentIdRef = useRef(Math.random().toString(36).substr(2, 9)); // Track component instances
  const { user, loading } = useAuth();
  

  
  // Initialize with user's areas if none are selected - but only after auth is complete
  // and only if user hasn't manually interacted with the filter
  useEffect(() => {
    // Don't initialize while auth is still loading
    if (loading) {
      return;
    }
    
    // Only auto-select user areas if:
    // 1. User hasn't manually interacted with the filter yet
    // 2. No areas are currently selected
    // 3. User has areas assigned
    if (!hasUserInteracted && 
        selectedAreas.length === 0 && 
        user?.areas && 
        Array.isArray(user?.areas) && 
        user?.areas.length > 0) {
      onChange(user.areas, false); // false = not user-initiated
    }
  }, [user?.areas, hasUserInteracted, loading]); // Include loading in dependencies

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper function to check if a group is fully selected
  const isGroupSelected = (group) => {
    return group.areas.every(area => selectedAreas.includes(area));
  };

  // Helper function to check if a group is partially selected
  const isGroupPartiallySelected = (group) => {
    return group.areas.some(area => selectedAreas.includes(area)) && !isGroupSelected(group);
  };

  const handleAreaToggle = (areaValue) => {
    // Check if this is a grouped selection
    const selectedGroup = Object.values(AREA_GROUPS).find(group => group.value === areaValue);
    
    if (selectedGroup) {
      // Handle group selection
      const isCurrentlySelected = isGroupSelected(selectedGroup);
      
      if (isCurrentlySelected) {
        // Deselect all areas in the group
        const newAreas = selectedAreas.filter(area => !selectedGroup.areas.includes(area));
        onChange(newAreas, true); // true = user-initiated
      } else {
        // Select all areas in the group (merge with existing selections)
        const newAreas = [...new Set([...selectedAreas, ...selectedGroup.areas])];
        onChange(newAreas, true); // true = user-initiated
      }
    } else {
      // Handle individual area selection
      if (selectedAreas.includes(areaValue)) {
        const newAreas = selectedAreas.filter(area => area !== areaValue);
        onChange(newAreas, true); // true = user-initiated
      } else {
        const newAreas = [...selectedAreas, areaValue];
        onChange(newAreas, true); // true = user-initiated
      }
    }
  };

  const handleClearAll = () => {
    onChange([], true); // true = user-initiated
  };

  // Get selected individual areas for display
  const selectedIndividualAreas = AVAILABLE_AREAS
    .filter(area => !area.isGroup && !area.isDivider && selectedAreas.includes(area.value));

  // Get selected groups for display
  const selectedGroups = Object.values(AREA_GROUPS).filter(group => isGroupSelected(group));

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor="areaFilter" className="block mb-1 text-sm text-gray-700">
        Areas
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="flex items-center truncate">
          {selectedAreas.length > 0 ? (
            <span className="flex items-center gap-1">
              {/* Show selected groups first */}
              {selectedGroups.map(group => (
                <span key={group.value} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {group.label}
                </span>
              ))}
              {/* Show individual areas that aren't part of selected groups */}
              {selectedIndividualAreas
                .filter(area => !selectedGroups.some(group => group.areas.includes(area.value)))
                .slice(0, selectedGroups.length === 0 ? 2 : 1)
                .map(area => (
                  <span key={area.value} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {area.label}
                  </span>
                ))
              }
              {/* Show count if there are more selections */}
              {(selectedGroups.length + selectedIndividualAreas.filter(area => !selectedGroups.some(group => group.areas.includes(area.value))).length) > 2 && (
                <span className="text-gray-500">
                  +{selectedAreas.length - (selectedGroups.length > 0 ? selectedGroups.length * 2 : 2)}
                </span>
              )}
            </span>
          ) : (
            'All Areas'
          )}
        </span>
        <svg
          className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div 
          className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
          role="listbox"
        >
          {AVAILABLE_AREAS.map((area, index) => {
            if (area.isDivider) {
              return (
                <div key={`divider-${index}`} className="border-t border-gray-200 my-1">
                  <div className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50">
                    Individual Areas
                  </div>
                </div>
              );
            }

            const isSelected = area.isGroup ? isGroupSelected(area) : selectedAreas.includes(area.value);
            const isPartiallySelected = area.isGroup ? isGroupPartiallySelected(area) : false;

            return (
              <div
                key={area.value}
                className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${
                  isSelected ? (area.isGroup ? 'bg-green-50' : 'bg-blue-50') : 
                  isPartiallySelected ? 'bg-yellow-50' : ''
                }`}
                onClick={() => handleAreaToggle(area.value)}
                role="option"
                aria-selected={isSelected}
              >
                <div className="flex items-center">
                  <span className={`block truncate ${area.isGroup ? 'font-medium' : ''}`}>
                    {area.isGroup && '🔗 '}{area.label}
                    {area.isGroup && (
                      <span className="text-xs text-gray-500 font-normal ml-1">
                        ({area.areas.length} areas)
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                      area.isGroup ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                  {isPartiallySelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-yellow-600">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


    </div>
  );
} 